import { u } from '../shared/hardcodedKoEn';
/**
 * '차트' 페이지 데이터 — 오늘 업비트 알림이 온 심볼들의 24시간 1분봉.
 *
 * analyser `GET /alert-binance-short-live/alert-charts` 가 봉을 **열(column)** 로
 * 준다: t0(첫 분) + step_s + o/h/l/c 배열. 빠진 분은 배열에 null 로 남는다 --
 * 그래야 수집 구멍이 "직선으로 이어진 가격"으로 보이지 않는다.
 *
 * 가격은 Binance perp **마크가격**이다 (업비트 원화 1분봉은 우리가 안 쌓는다).
 * 알림 마커의 px 만 업비트가 알림에 실어 준 원화 체결가다.
 */

export type AlertChartMarker = {
  /** epoch ms */
  t: number;
  type: string;
  exchange: string;
  /** 알림에 실린 원화 체결가 (있을 때만) */
  px?: number;
  /** 알림 급등폭 % (signal_alert.segment_pct, 있을 때만) — 업숏 */
  seg?: number;
  /** 청캐(liq) 전용 — 그 발동의 청산 롤 노셔널(USD)과 실제 진입 여부.
   *  entered=false 면 type 에 skip_reason 이 온다(no_pullback·hot_ban…). */
  notional_usd?: number;
  entered?: boolean;
  /** 청캐 — 이 발동의 개별 청산 건수 (liq_trigger.n_liq). 캐스케이드(quiet6·
   *  산리셋) 시작부터 누적해 화살표에 표시(2026-09-09, 옛 "N회" 트리거 수 대체). */
  n_liq?: number;
};

export type AlertChartSeries = {
  symbol: string;
  perp: string;
  alerts: AlertChartMarker[];
  /** 첫 분의 epoch ms */
  t0: number;
  step_s: number;
  o: (number | null)[] | null;
  h: (number | null)[] | null;
  l: (number | null)[] | null;
  c: (number | null)[] | null;
  n_bars: number;
  last_px?: number;
  chg_pct?: number;
  high_px?: number;
  low_px?: number;
  sources?: string[];
  /** 그릴 게 없을 때 이유 · 가격이 멈춰 있을 때 이유(둘 다 여기 한 줄로 옴) */
  note?: string;
  /** 서버가 24시간치를 백그라운드로 펴는 중 — 지금 선은 반쪽일 수 있다 */
  warming?: boolean;
  /** 마지막 실제 봉이 몇 분 전인가 — 이게 있으면 봉은 있어도(n_bars>0) 오래된
   *  값이다. 상시수집 대상이 아니거나 상장폐지된 심볼(예: WAVES, ws_batch 평생
   *  0건)이 이렇게 나온다. */
  stale_min?: number;
};

export type AlertChartsResponse = {
  ok: boolean;
  generated_at: string;
  kst_day: string;
  window_h: number;
  took_ms: number;
  n_symbols: number;
  truncated?: boolean;
  series: AlertChartSeries[];
  error?: string;
  /** 창의 실제 경계(epoch ms) — 라이브·과거창 둘 다 채워진다. */
  from_ms: number;
  to_ms: number;
  /** true 면 사다리 날짜 보기. false/미정이면 라이브(오늘~지금). */
  historic?: boolean;
  center_ms?: number;
  /** 클러스터(quiet6) 시작·끝 — 사다리 버튼이 고른 클러스터. */
  cluster_first_ms?: number;
  cluster_last_ms?: number;
  /** 창 안 산 목록. 업숏은 구간이 올라가는 급등(start_seg→end_seg). */
  mountains?: Array<{
    first_ms: number; last_ms: number; max_rung?: number;
    start_seg?: number; end_seg?: number;
  }>;
  /** 업숏 15분·120분 확인 진입·청산. */
  waits?: Array<{ wait_min: number; entry_ms: number; exit_ms: number; seg?: number }>;
  /** 과거창이 아직 백그라운드로 만들어지는 중 — series 는 비어 있다. 잠시 뒤 재요청. */
  warming?: boolean;
};

/** lightweight-charts 라인 포인트. value 가 없으면 whitespace(선이 끊긴다). */
export type AlertChartPoint = { time: number; value?: number };

/**
 * 라인에 쓸 봉 가격.
 *   c — 종가 (기본)
 *   h — 고가 (청캐 버튼% = 시가→고가. last 윅도 보이게)
 */
export type ChartPxField = 'c' | 'h';

function seriesPxCol(s: AlertChartSeries, field: ChartPxField): (number | null)[] {
  if (field === 'h') {
    const highs = s.h ?? [];
    if (highs.some((v) => v != null && Number.isFinite(v) && v > 0)) return highs;
  }
  return s.c ?? [];
}

/**
 * 열 배열 → 라인 포인트. **빠진 분도 whitespace 로 넣는다**: 그래야
 * (a) 선이 구멍을 건너뛰어 이어지지 않고 (b) 심볼마다 x축이 같은 24시간이라
 * 카드끼리 시각을 눈으로 맞출 수 있다.
 */
export function alertSeriesPoints(
  s: AlertChartSeries, field: ChartPxField = 'c',
): AlertChartPoint[] {
  const vals = seriesPxCol(s, field);
  if (!vals.length) return [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const out: AlertChartPoint[] = [];
  for (let i = 0; i < vals.length; i += 1) {
    const time = t0 + i * step;
    const v = vals[i];
    out.push(v == null || !Number.isFinite(v) ? { time } : { time, value: v });
  }
  return out;
}

/**
 * 봉 격자에 맞춘 알림/발동 한 건.
 *   seg          — 업숏: 그 분에서 가장 큰 급등폭 %(없으면 null)
 *   entered      — 청캐: 그 분에 실제 진입이 있었나(하나라도 있으면 true)
 *   markerType   — 청캐: entered 가 없으면 스킵 사유(마지막 값), 있으면 'entered'
 *   notionalUsd  — 청캐: entered 우선, 없으면 마지막 발동의 청산 롤 노셔널
 */
export type AlertMarkerPoint = {
  time: number;
  seg: number | null;
  entered: boolean | null;
  markerType: string | null;
  notionalUsd: number | null;
  /** 이 봉에 접힌 원(raw) 사건 수. */
  count: number;
  /** 이 봉의 개별 청산 건수 합(청캐). 화살표가 캐스케이드(quiet6·산리셋)
   *  시작부터 누적해 "누적 N"(옛 "N회" 대체) 을 매긴다. n_liq 없는 발동은 1. */
  nliq: number;
  /** 진입·청산·스탑의 실제 시각(epoch ms). 봉에 내림한 time 과 다를 수 있다. */
  eventMs?: number;
};

/**
 * 알림/발동 시각 → 봉 격자 위의 초 + 대표값. 창 밖(어제 것)은 버린다. 같은
 * 분에 여러 건이면 마커 하나로 접는다(같은 자리에 화살표를 겹쳐 그리면 축만
 * 지저분해진다) — 업숏은 급등폭이 **가장 큰 값**, 청캐는 **진입이 있었으면
 * 그 발동**(스킵보다 우선), 없으면 마지막 발동을 남긴다.
 */
export function alertMarkerPoints(s: AlertChartSeries): AlertMarkerPoint[] {
  const closes = s.c ?? [];
  if (!closes.length) return [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const end = t0 + (closes.length - 1) * step;
  const bySnap = new Map<number, AlertMarkerPoint>();
  const order: number[] = [];
  for (const a of s.alerts ?? []) {
    if (!Number.isFinite(a.t)) continue;
    const sec = Math.floor(a.t / 1000);
    // 반올림이 아니라 **내림**: 사건은 그 시각이 든 봉에 붙어야 한다
    // (00:01:35 는 00:02 봉이 아니라 00:01 봉의 사건이다).
    const snapped = t0 + Math.floor((sec - t0) / step) * step;
    if (snapped < t0 || snapped > end) continue;
    const seg = typeof a.seg === 'number' && Number.isFinite(a.seg) ? a.seg : null;
    const entered = typeof a.entered === 'boolean' ? a.entered : null;
    const notionalUsd = typeof a.notional_usd === 'number' && Number.isFinite(a.notional_usd)
      ? a.notional_usd : null;
    const nliq = typeof a.n_liq === 'number' && Number.isFinite(a.n_liq) && a.n_liq > 0
      ? a.n_liq : 1;
    const trade = a.type === 'entry' || a.type === 'exit' || a.type === 'stop';
    const prev = bySnap.get(snapped);
    if (!prev) {
      bySnap.set(snapped, {
        time: snapped, seg, entered, markerType: a.type ?? null, notionalUsd,
        count: 1, nliq, ...(trade ? { eventMs: a.t } : {}),
      });
      order.push(snapped);
      continue;
    }
    prev.count += 1;
    prev.nliq += nliq;
    // 급등폭은 더 큰 값이 이긴다(업숏). 진입·청산·스탑은 같은 분 알림보다 이긴다.
    // 청캐는 진입이 있었으면 그 발동을 남긴다(스킵된 watch 보다 우선).
    if (seg != null && (prev.seg == null || seg > prev.seg)) prev.seg = seg;
    const prevTrade = prev.markerType === 'entry' || prev.markerType === 'exit'
      || prev.markerType === 'stop';
    if (trade && !prevTrade) {
      prev.markerType = a.type;
      prev.eventMs = a.t;
    } else if (!prevTrade && entered) {
      prev.entered = true;
      prev.markerType = a.type ?? prev.markerType;
      prev.notionalUsd = notionalUsd ?? prev.notionalUsd;
    } else if (!prevTrade && !prev.entered) {
      // 아직 진입을 못 봤으면(계속 false/null) 이 분의 스킵 사유를 **최신
      // 것으로** 계속 갱신한다 — 한 번 true 가 됐으면(위 분기) 더는 안 건드린다.
      prev.entered = entered;
      prev.markerType = a.type ?? prev.markerType;
      prev.notionalUsd = notionalUsd ?? prev.notionalUsd;
    }
  }
  return order.sort((a, b) => a - b).map((t) => bySnap.get(t)!);
}

/**
 * 알림 시각(초)만. 창 밖 알림은 버리고 같은 분은 하나로 접는다.
 */
export function alertMarkerTimes(s: AlertChartSeries): number[] {
  return alertMarkerPoints(s).map((p) => p.time);
}

/**
 * 같은 분 접기가 화살표 글자에서 뺀 급등 %.
 * 진입·청산·스탑이 그 분을 가져가면 그 분의 % 를 전부 돌려주고,
 * % 만 있으면 가장 큰 값만 이미 보이므로 나머지만 돌려준다.
 */
export function upbitSegsBesideMarker(s: AlertChartSeries): Array<{ time: number; seg: number }> {
  const closes = s.c ?? [];
  if (!closes.length) return [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const end = t0 + (closes.length - 1) * step;
  const byTime = new Map(alertMarkerPoints(s).map((p) => [p.time, p]));
  const seen = new Set<string>();
  const out: Array<{ time: number; seg: number }> = [];
  for (const a of s.alerts ?? []) {
    if (a.type === 'entry' || a.type === 'exit' || a.type === 'stop') continue;
    if (typeof a.seg !== 'number' || !(a.seg > 0) || !Number.isFinite(a.seg)) continue;
    const sec = Math.floor(a.t / 1000);
    const snapped = t0 + Math.floor((sec - t0) / step) * step;
    if (snapped < t0 || snapped > end) continue;
    const key = `${snapped}|${a.seg}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const prev = byTime.get(snapped);
    const trade = prev?.markerType === 'entry' || prev?.markerType === 'exit' || prev?.markerType === 'stop';
    if (!trade && prev?.seg === a.seg) continue;
    out.push({ time: snapped, seg: a.seg });
  }
  out.sort((a, b) => a.time - b.time || a.seg - b.seg);
  return out;
}


// --- 레인 --------------------------------------------------------------------

/**
 * 레인 정체색. 피드·주간표·Ex 칸이 쓰는 값과 **같은 값**이어야 한다
 * (BinanceAlertShortLivePanel 의 EXCHANGE_COLOR). 여기서 새 색을 만들면 같은
 * 레인이 화면마다 다른 색으로 보인다.
 */
export const LANE_COLORS = {
  upbit: '#4d94ff',
  bh: '#ec7000',
  liq: '#f0b90b',
  gyeop: '#a371f7',
} as const;

export type LaneKey = keyof typeof LANE_COLORS;

export const LANES: { key: LaneKey; label: string; ready: boolean }[] = [
  { key: 'upbit', label: u('업숏'), ready: true },
  { key: 'bh', label: u('빗숏'), ready: true },
  { key: 'liq', label: u('청캐'), ready: true },
  { key: 'gyeop', label: u('겹롱'), ready: true },
];

/** 청캐 스킵 사유 → 짧은 한글 라벨. 화살표 텍스트·범례 툴팁에 쓴다. 목록은
 *  alert_binance_short_live.py 의 update_liq_trigger(skip_reason=...) 호출부와
 *  같다 — 새 사유가 생기면 여기 추가할 것(없으면 코드 그대로 보여준다). */
const LIQ_SKIP_LABELS: Record<string, string> = {
  no_pullback: u('미되돌림'),
  hot_ban: u('재숏금지'),
  kr_segment_high: u('KR급등'),
  no_fade: u('미fade'),
  scout_run_hi: u('런업高'),
  not_live: u('레인OFF'),
  slot_full: u('슬롯가득'),
  margin: u('증거금부족'),
  confirm_reject: u('확인거절'),
  entry_fail: u('진입실패'),
  lane_busy: u('락중'),
  no_filter: u('필터無'),
  watch: u('감시중'),
};

/** 청캐 화살표/범례 라벨 — 진입이면 u('진입'), 아니면 스킵 사유. */
export function liqMarkerLabel(p: Pick<AlertMarkerPoint, 'entered' | 'markerType'>): string {
  if (p.entered) return u('진입');
  if (!p.markerType) return '';
  return LIQ_SKIP_LABELS[p.markerType] ?? p.markerType;
}

/** 캐스케이드 경계(초) — 직전 발동이 이만큼 넘게 안 떨어졌으면 새 사슬.
 *  백엔드 CASCADE_QUIET_H(6h)·`ladder_order` 와 같아야 한다. */
export const CASCADE_QUIET_S = 6 * 3600;

/**
 * 청캐 마커별 누적 개별청산(Σ n_liq) 라벨.
 * 리셋 = **6h quiet** 또는 **산리셋 경계** (`mountainStartsSec` = API mountains
 * 의 first_ms/1000, 산2+). 산 경계를 안 주면 예전처럼 quiet6 만 — 산±24h
 * 차트에선 라이브 `cascade_span_from_rows` 와 맞춰야 산2 첫 발동이
 * "rung 6 (164)" 로 안 보인다 (2026-09-30 QNT).
 * ≥100 이면 `rung ${cum//25} (${cum})`, 아니면 `누적 ${cum}`.
 */
export function liqCascadeCumLabels(
  markers: Pick<AlertMarkerPoint, 'time' | 'nliq'>[],
  opts?: { mountainStartsSec?: number[]; cumWord?: string },
): string[] {
  const cumWord = opts?.cumWord ?? u('누적');
  const breaks = (opts?.mountainStartsSec ?? [])
    .filter((t) => Number.isFinite(t) && t > 0)
    .sort((a, b) => a - b);
  let cum = 0;
  let prevT: number | null = null;
  return markers.map((p) => {
    if (prevT != null) {
      if (p.time - prevT > CASCADE_QUIET_S) cum = 0;
      else if (breaks.some((b) => prevT! < b && p.time >= b)) cum = 0;
    }
    prevT = p.time;
    cum += p.nliq > 0 ? p.nliq : 1;
    return cum >= 100 ? `rung ${Math.floor(cum / 25)} (${cum})` : `${cumWord} ${cum}`;
  });
}

// --- 사다리 날짜 버튼 ----------------------------------------------------------

/**
 * analyser `GET /alert-binance-short-live/ladder-days` 한 항목.
 * 업숏은 채점 책에서 가격%가 음수인 건(`net_pct`). 청캐는 rung, 빗숏은 스탑.
 * `first_ms`…`last_ms` 앞뒤 24h 가 차트 창.
 */
export type LadderDayItem = {
  symbol: string;
  perp: string;
  max_rung: number;
  /** 업숏·겹롱=가격%. 청캐=산 구간 시가→고가%. Vision strict=72h ret%. */
  net_pct?: number;
  center_ms: number;
  first_ms: number;
  last_ms: number;
};

export type LadderDaysResponse = {
  ok: boolean;
  lane: LaneKey;
  min_rung: number;
  episodes: LadderDayItem[];
  error?: string;
};

/** Vision 피어 버튼 창 = 스캔 LOOKBACK 72h (asof 직전). */
const VISION_PEER_LOOKBACK_MS = 72 * 3_600_000;

function visionPeer(perp: string, netPct: number, asofKst: string): LadderDayItem {
  const last = Date.parse(asofKst);
  const first = last - VISION_PEER_LOOKBACK_MS;
  return {
    symbol: perp, perp, max_rung: 0, net_pct: netPct,
    center_ms: last, first_ms: first, last_ms: last,
  };
}

/**
 * Vision 1–7월 시가→고가 ≥300% (2026-10-24 class-mag 스캔).
 * 옛 ret≥200 가짜 14개 버튼은 넣지 않음.
 * 창 = asof 직전 72h. net_pct = open→max high.
 */
export const LIQ_VISION_STRICT_PEERS: LadderDayItem[] = [
  visionPeer('BABYUSDT', 10936, '2026-06-08T18:00:00+09:00'),
  visionPeer('STOUSDT', 1399, '2026-04-04T00:00:00+09:00'),
  visionPeer('BULLAUSDT', 942, '2026-02-01T12:00:00+09:00'),
  visionPeer('AKEUSDT', 933, '2026-07-18T12:00:00+09:00'),
  visionPeer('RAVEUSDT', 785, '2026-04-15T12:00:00+09:00'),
  visionPeer('TAIKOUSDT', 734, '2026-07-02T18:00:00+09:00'),
  visionPeer('PORTALUSDT', 567, '2026-06-01T18:00:00+09:00'),
  visionPeer('SIRENUSDT', 516, '2026-03-23T06:00:00+09:00'),
  visionPeer('LABUSDT', 509, '2026-05-03T12:00:00+09:00'),
  visionPeer('VELVETUSDT', 499, '2026-06-12T12:00:00+09:00'),
  visionPeer('BLESSUSDT', 497, '2026-04-14T12:00:00+09:00'),
  visionPeer('POWERUSDT', 441, '2026-02-27T00:00:00+09:00'),
  visionPeer('BASEDUSDT', 416, '2026-04-17T18:00:00+09:00'),
  visionPeer('AIOTUSDT', 410, '2026-04-03T12:00:00+09:00'),
  visionPeer('BSBUSDT', 398, '2026-05-21T12:00:00+09:00'),
  visionPeer('ESPORTSUSDT', 396, '2026-06-13T12:00:00+09:00'),
  visionPeer('BRUSDT', 390, '2026-03-23T12:00:00+09:00'),
  visionPeer('BANKUSDT', 382, '2026-07-21T18:00:00+09:00'),
  visionPeer('HUSDT', 357, '2026-06-14T18:00:00+09:00'),
  visionPeer('CHIPUSDT', 353, '2026-04-24T12:00:00+09:00'),
  visionPeer('EVAAUSDT', 349, '2026-07-09T12:00:00+09:00'),
  visionPeer('ORDIUSDT', 344, '2026-04-18T06:00:00+09:00'),
  visionPeer('BTWUSDT', 340, '2026-06-21T12:00:00+09:00'),
  visionPeer('BUSDT', 324, '2026-05-04T12:00:00+09:00'),
  visionPeer('ALLOUSDT', 321, '2026-05-30T06:00:00+09:00'),
  visionPeer('DUSKUSDT', 308, '2026-01-19T18:00:00+09:00'),
];

/** Vision ≥300% 피어 버튼색 — LSK·IRYS와 같은 빨강. */
export const LIQ_VISION_PEER_COLOR = '#f85149';

const KST_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit',
});

/** epoch ms → "09-06" (KST). 날짜 버튼 라벨의 앞부분. */
export function kstMonthDay(ms: number): string {
  return KST_DATE_FMT.format(new Date(ms));
}

// --- 색 계열 ------------------------------------------------------------------

function hexToHsl(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0, 0, 50];
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, sat * 100, l * 100];
}

/**
 * 한 레인 색에서 심볼 수만큼 **같은 계열의** 색을 만든다.
 *
 * 유저 요구가 "업숏 색깔로 차트들이 나와야" 이므로 색상(hue)은 레인에 묶어 두고
 * 밝기로 가른다. 다만 밝기를 i 순서대로 나열하면 이웃한 두 선이 거의 같은 색이
 * 되어 14개를 못 가린다 -- 황금비로 흩어 인접 심볼이 멀리 떨어지게 한다.
 */
/**
 * %가 가장 높은 심볼 = 레인 정체색, 낮아질수록 옅어져 **가장 낮은 심볼 = 흰색**.
 * (2026-09-08 유저 요구 — hue 는 레인에 고정, 순위로 채도·명도만 가른다.)
 *
 * `metrics[i]` 는 그 심볼의 현재 % (라인 마지막 값). 값 자체가 아니라 **순위**로
 * 나눈다 — 한 종목만 크게 튀어도 나머지가 죄다 흰색으로 뭉치지 않게. 값이 없는
 * (null) 심볼은 최하위로 본다.
 */
export function pctRankColors(baseHex: string, metrics: (number | null)[]): string[] {
  const [h, s, l] = hexToHsl(baseHex);
  const n = metrics.length;
  const order = metrics
    .map((m, i) => ({ i, v: m != null && Number.isFinite(m) ? m : -Infinity }))
    .sort((a, b) => a.v - b.v);
  const rank = new Array<number>(n);
  order.forEach((o, pos) => { rank[o.i] = pos; });
  return metrics.map((_, i) => {
    const t = n <= 1 ? 1 : rank[i] / (n - 1); // 0 = 최저 %, 1 = 최고 %
    const sat = s * t;                        // t=1 → 레인색 채도, t=0 → 0%(무채색)
    const light = l + (100 - l) * (1 - t);    // t=1 → 레인색 명도, t=0 → 100%(흰색)
    return `hsl(${h.toFixed(0)} ${Math.round(sat)}% ${Math.round(light)}%)`;
  });
}

export function laneShades(baseHex: string, n: number): string[] {
  const [h0, s0] = hexToHsl(baseHex);
  const out: string[] = [];
  for (let i = 0; i < n; i += 1) {
    const tL = n <= 1 ? 0.5 : (i * 0.6180339887) % 1;
    const tH = (i * 0.3819660113) % 1;
    const l = 36 + 44 * tL;                 // 36%~80% — 어두운 배경에서 다 읽힌다
    const h = (h0 + (tH - 0.5) * 26 + 360) % 360; // ±13° — 계열은 유지
    const sat = Math.min(100, s0 * (i % 2 === 0 ? 1 : 0.86));
    out.push(`hsl(${h.toFixed(0)} ${sat.toFixed(0)}% ${l.toFixed(0)}%)`);
  }
  return out;
}

// --- 한 차트로 합치기 ---------------------------------------------------------

/** 여러 심볼을 한 축에 올리려면 가격을 %로 바꿔야 한다. 그 기준을 무엇으로 둘까. */
export type BaselineMode = 'window' | 'alert' | 'peak';

/**
 * %의 기준가.
 *   window = 창 안 첫 봉. 고가 라인(h)이면 **첫 시가(o)** — 버튼 시가→고가와 동일.
 *   alert  = 그 심볼의 **첫 알림 분** (0% = 알림 시각 → 레인이 보는 그림)
 *   peak   = 창 안 **최고가** (라인 필드 기준; h면 윅 포함)
 * 기준을 못 잡으면(봉이 없으면) null.
 */
export function alertBaselinePx(
  s: AlertChartSeries, mode: BaselineMode, field: ChartPxField = 'c',
): number | null {
  const line = seriesPxCol(s, field);
  if (!line.length) return null;
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  if (mode === 'peak') {
    let hi: number | null = null;
    for (const v of line) {
      if (v != null && Number.isFinite(v) && v > 0 && (hi == null || v > hi)) hi = v;
    }
    return hi;
  }
  if (mode === 'alert') {
    const times = alertMarkerTimes(s);
    if (times.length) {
      // 알림 분에 봉이 비어 있으면(수집 구멍) 그 뒤 첫 봉으로 민다.
      for (let i = Math.max(0, Math.round((times[0] - t0) / step)); i < line.length; i += 1) {
        const v = line[i];
        if (v != null && Number.isFinite(v) && v > 0) return v;
      }
    }
  }
  // window: 고가 라인은 시가(o) 기준 — BABY last 윅 +10936% 과 같은 축.
  if (field === 'h') {
    const opens = s.o ?? [];
    for (let i = 0; i < opens.length; i += 1) {
      const v = opens[i];
      if (v != null && Number.isFinite(v) && v > 0) return v;
    }
  }
  for (let i = 0; i < line.length; i += 1) {
    const v = line[i];
    if (v != null && Number.isFinite(v) && v > 0) return v;
  }
  return null;
}

/** 기준가 대비 % 라인. 기준을 못 잡으면 전부 whitespace(선을 안 그린다). */
export function alertSeriesPercentPoints(
  s: AlertChartSeries, mode: BaselineMode, field: ChartPxField = 'c',
): AlertChartPoint[] {
  const pts = alertSeriesPoints(s, field);
  const base = alertBaselinePx(s, mode, field);
  if (base == null) return pts.map((p) => ({ time: p.time }));
  return pts.map((p) => (
    p.value == null ? { time: p.time } : { time: p.time, value: (p.value / base - 1) * 100 }
  ));
}

/**
 * 기준가 대비 **배율** 라인 (1.0 = 기준). 로그 축용 — 세로축이 선형 %면 기준가가
 * 한참 아래일 때 같은 낙폭이 더 크게 그려진다 (rung12 +120% → +42% 로 그려진
 * 실제 낙폭은 −34% 다. 142/215−1). 배율 + 로그축이면 −30% 는 차트 어디서든
 * 같은 픽셀 높이로 떨어진다. 값 → % 는 `(v−1)×100`.
 * 청캐는 field='h' (시가→고가 = 버튼%).
 */
export function alertSeriesRatioPoints(
  s: AlertChartSeries, mode: BaselineMode, field: ChartPxField = 'c',
): AlertChartPoint[] {
  const pts = alertSeriesPoints(s, field);
  const base = alertBaselinePx(s, mode, field);
  if (base == null) return pts.map((p) => ({ time: p.time }));
  return pts.map((p) => (
    p.value == null || p.value <= 0
      ? { time: p.time }
      : { time: p.time, value: p.value / base }
  ));
}

/** 가격 자릿수 — 알트는 0.00001 대까지 간다. 유효숫자 5개 정도로 맞춘다. */
export function fmtPrice(v?: number | null): string {
  if (v == null || !Number.isFinite(v)) return '–';
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(3);
  if (abs >= 0.01) return v.toFixed(5);
  return v.toPrecision(4);
}

/** 차트 위 진입·청산 시각. KST `08-13 18:41`. */
export function fmtMarkerClock(ms: number): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms));
  const g = (typ: string) => parts.find((p) => p.type === typ)?.value ?? '';
  return `${g('month')}-${g('day')} ${g('hour')}:${g('minute')}`;
}

export function fmtPct(v?: number | null): string {
  if (v == null || !Number.isFinite(v)) return '–';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

/** 화살표 라벨용 급등폭 — 짧게. 10%↑ 는 정수, 그 아래는 소수 한 자리. */
export function fmtAlertSeg(v?: number | null): string {
  if (v == null || !Number.isFinite(v)) return '';
  return `+${Math.abs(v) >= 10 ? Math.round(v) : v.toFixed(1)}%`;
}

/**
 * analyserFetchJSON 이 내부 타임아웃/네트워크 실패, 또는 프록시 게이트웨이
 * 오류(502/503/504)일 때 던지는 Error.message 문구인가 — 그렇다면 "실패"가
 * 아니라 "아직 로딩 중"으로 본다. 48h 과거창은 서버가 백그라운드로 만드는
 * 동안 CloudFront(origin_read_timeout 30s)가 먼저 끊어 504 HTML 을 줄 수
 * 있는데, 매번 빨간 에러로 보여주면 사용자가 다시 누르기 전엔 안 뜬 것처럼
 * 보인다 (2026-09-08 유저: "에러 내지 말고 그냥 로딩중이라고 보여줘"). 문구는
 * analyserApi.ts 의 throw 지점들과 정확히 맞춰야 한다.
 */
export function isTransientLoadError(msg: string): boolean {
  return /시간 초과|네트워크 오류|HTTP 50[234]/.test(msg);
}
